import React, { useState, useRef, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "react-hot-toast";
import API from "../api";
import { capture } from "../instrument";

const QuestionLookup = ({ onChange, initialValue = null, placeholder = "Rechercher une question...", answerType = null, excludeAnswerTypes = [] }) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionSuggestions, setQuestionSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const suggestionsRef = useRef(null);
  const suggestionItemsRef = useRef([]);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize selected question from initialValue
  useEffect(() => {
    if (initialValue) getQuestion(initialValue);
  }, [initialValue]);

  // Auto-scroll to keep selected item in view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionItemsRef.current[selectedIndex]) {
      suggestionItemsRef.current[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  // Reset refs array when suggestions change
  useEffect(() => {
    suggestionItemsRef.current = suggestionItemsRef.current.slice(0, questionSuggestions.length);
  }, [questionSuggestions]);

  const getQuestion = async (id) => {
    try {
      const response = await API.get(`/question/${id}`);
      if (!response.ok) return toast.error(response.message);
      setSelectedQuestion(response.data);
    } catch (error) {
      if (error?.code === "QUESTION_NOT_FOUND") return toast.error("Question introuvable");
      capture(error);
      console.log(error);
      toast.error("Une erreur est survenue lors de la récupération de la question");
    }
  };

  const fetchQuestionSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setQuestionSuggestions([]);
      return;
    }

    try {
      setIsLoading(true);

      const response = await API.get(`/question?q=${query}${answerType ? `&answerType=${answerType}` : ""}`);
      if (!response.ok) return toast.error(response.message);
      const results = excludeAnswerTypes.length > 0 ? response.data.filter((q) => !excludeAnswerTypes.includes(q.answerType)) : response.data;
      setQuestionSuggestions(results);
      setShowSuggestions(true);
      if (results.length > 0) setSelectedIndex(0);
      else setSelectedIndex(-1);
    } catch (error) {
      capture(error);
      console.log(error);
      toast.error("Une erreur est survenue");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Add debounce for better performance
  useEffect(() => {
    if (inputValue?.length < 2) return;

    const debounceTimer = setTimeout(() => fetchQuestionSuggestions(inputValue), 200);

    return () => clearTimeout(debounceTimer);
  }, [inputValue, answerType]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const selectQuestion = (question) => {
    setSelectedQuestion(question);
    if (onChange) onChange(question);

    setInputValue("");
    setQuestionSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);

    // Focus back on the input with a small delay
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
  };

  const removeSelectedQuestion = () => {
    setSelectedQuestion(null);
    if (onChange) onChange(null);

    // Focus back on the input with a small delay
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
  };

  const navOpts = { enabled: showSuggestions && questionSuggestions.length > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedIndex((i) => Math.min(i + 1, questionSuggestions.length - 1)), navOpts, [showSuggestions, questionSuggestions]);
  useHotkeys("up", () => setSelectedIndex((i) => Math.max(i - 1, 0)), navOpts, [showSuggestions, questionSuggestions]);
  useHotkeys("enter", () => selectQuestion(questionSuggestions[selectedIndex >= 0 ? selectedIndex : 0]), navOpts, [showSuggestions, questionSuggestions, selectedIndex]);
  useHotkeys("escape", () => { setShowSuggestions(false); setSelectedIndex(-1); }, navOpts, [showSuggestions, questionSuggestions]);

  const handleKeyDown = (e) => {
    if (showSuggestions && questionSuggestions.length > 0) return;
    if (e.key === "Backspace" && inputValue === "" && selectedQuestion) {
      removeSelectedQuestion();
    }
  };

  const handleContainerClick = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="bg-white flex flex-wrap items-center gap-1 p-2 border rounded-lg focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-1 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all duration-200 min-h-[42px] cursor-text"
        onClick={handleContainerClick}
      >
        {selectedQuestion ? (
          <div className="flex items-center justify-between w-full px-1">
            <div className="flex flex-col cursor-text w-[calc(100%-24px)]">
              <span className="font-medium hover:text-primary-500 cursor-pointer" onClick={() => window.open(`/question/${selectedQuestion._id}/edit`, "_blank")}>
                {selectedQuestion.questionCode || selectedQuestion._id}
              </span>
              <span className="text-xs text-gray-600 truncate">{selectedQuestion.question || ""}</span>
            </div>
            <button
              className="ml-1 text-primary-600 hover:text-primary-800 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                removeSelectedQuestion();
              }}
              type="button"
              aria-label={`Remove ${selectedQuestion.questionCode || selectedQuestion._id}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex items-center flex-grow">
            <input
              ref={inputRef}
              className="flex-grow outline-none min-w-[120px]"
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={() =>
                setTimeout(() => {
                  setShowSuggestions(false);
                  setSelectedIndex(-1);
                }, 200)
              }
              onFocus={() => {
                if (inputValue.length >= 2) {
                  fetchQuestionSuggestions(inputValue);
                }
              }}
            />
            {isLoading && <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full mr-2"></div>}
          </div>
        )}
      </div>

      {questionSuggestions.length > 0 && (
        <div ref={suggestionsRef} className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto max-h-60">
          {questionSuggestions.map((question, idx) => (
            <div
              ref={(el) => (suggestionItemsRef.current[idx] = el)}
              key={idx}
              className={`p-3 cursor-pointer flex justify-between items-center gap-2 border-b border-gray-100 last:border-b-0 ${
                selectedIndex === idx ? "bg-primary-50 text-primary-700" : "hover:bg-gray-50"
              }`}
              onClick={() => selectQuestion(question)}
            >
              <div className="flex flex-col flex-grow overflow-hidden">
                <span className="font-medium">{question.questionCode || question._id}</span>
                <span className="text-sm text-gray-500 truncate max-w-full">{question.question || ""}</span>
              </div>
              {selectedIndex === idx && <div className="text-primary-500 bg-primary-100 rounded px-2 py-1 text-xs font-medium whitespace-nowrap">Enter ↵</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionLookup;
